import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NativeAudio } from '@ionic-native/native-audio/ngx';
import { AlertController, Platform } from '@ionic/angular';
import { Subscription, interval } from 'rxjs';
import { debounceTime, startWith } from 'rxjs/operators';
import { WatcherService } from 'src/app/api/utils/watcher/watcher.service';
import { Order } from 'src/app/models/order.model';
import { AnalyticsService } from '../../utils/analytics/analytics.service';
import { CommsService } from '../../utils/comms/comms.service';
import { AlertQueueService } from '../../utils/prompt-queue/alert-queue.service';
import {
  ACTIVE_ORDER_UPDATED,
  APP_DOCKET_DISPLAY_UPDATE,
  AUTO_ETA_PRINTING_ORDER,
  AUTO_PRINTING_ORDER,
  ETA_PROMPT,
  ORDER_COUNT_CHANGED,
  PLAY_SOUND_ETA,
  PLAY_SOUND_NEW,
  POLL_COMPLETED,
  POLL_UPDATE,
} from '../../utils/watcher/watcher-event';
import { AuthService } from '../auth/auth.service';
import { CoreService } from '../core/core.service';
import { DocketFilterService, IPreorderWeek } from '../docket-filter/docket-filter.service';
import { SettingsService } from '../settings/settings.service';

import * as moment from 'moment-timezone';

import countBy from 'lodash/countBy';
import each from 'lodash/each';
import filter from 'lodash/filter';
import find from 'lodash/find';
import findIndex from 'lodash/findIndex';
import findLast from 'lodash/findLast';
import has from 'lodash/has';
import isNil from 'lodash/isNil';
import map from 'lodash/map';
import size from 'lodash/size';
import { BffService } from '../../utils/comms/bff/bff.service';
import { OMBffSocketEventType, SocketioService } from '../../utils/comms/socketio/socketio.service';
import { UtilityClass } from '../../utils/UtilityClass';
import { VipService } from '../insights/vip.service';
import { FeatureFlagList, FeatureFlagsService } from '../settings/feature-flags.service';
import { GeneratedDocketRepositoryService } from './repository/generated-docket-repository.service';

export enum CONTEXT_TYPE {
  Delivery = 'delivery',
  Pickup = 'onlinepickup',
  Dine_in = 'dinein',
  Room_service = 'room_service',
  Drive_up = 'drive_up',
}

@Injectable({
  providedIn: 'root',
})
export class DocketService {
  /** The list of all available orders */
  private _orders: Order[] = [];

  /** The interval of order polling in seconds */
  private _bump_frequency: number = 240000;

  /** The polling interval subscription */
  private _interval: Subscription;

  /** The polling request subscription */
  private _pollSub: Subscription;

  /** The Socket IO Connect subscription */
  private _socketIOSub: Subscription;

  /** The subscription for auto set eta docket generation */
  private _autoSetETADocketSub: Subscription;

  /** Flagger to indicate polling status */
  private _polling: boolean = false;

  /** Flagger for when the polling has stopped */
  private _stopped: boolean = true;

  /** The selected order and mostly in view */
  private _active_order: Order;

  public firstPollComplete: boolean = false;

  public audio: any;
  public audio2: any;

  public ETACheckRef: any;
  public MAX_ETA_INTERVAL: number = 60000;
  public MID_ETA_INTERVAL: number = 20000;
  public MIN_ETA_INTERVAL: number = 10000;
  public ETA_INTERVAL: number = this.MIN_ETA_INTERVAL;

  public ETA_MINUTE_CHECK: number = 1;
  public ETA_LOOP_COUNTER: number = 1;

  private _pendingOrdersWithNoEta: { id: string; viewed: boolean }[] = [];
  private _pendingDineInToAutoPrint: {
    id: string;
    printed: boolean;
    failed: boolean;
  }[] = [];

  private _pendingAutoSetETAtoPrint: {
    id: string;
    printed: boolean;
    failed: boolean;
  }[] = [];

  constructor(
    public watcher: WatcherService,
    public comms: CommsService,
    public auth: AuthService,
    public core: CoreService,
    public filterService: DocketFilterService,
    public alertCtrl: AlertController,
    public alertQueue: AlertQueueService,
    public nativeAudio: NativeAudio,
    public platform: Platform,
    public appSettings: SettingsService,
    public socketIO: SocketioService,
    public analytics: AnalyticsService,
    public featureFlag: FeatureFlagsService,
    public generatedDocketRepo: GeneratedDocketRepositoryService,
    public bffService: BffService,
    public vipService: VipService,
  ) {
    platform.ready().then(ready => {
      //try to create Native Audio
      this.checkNativeAudio();

      //set the fallback (for Web) audio file.
      // no ETA
      this.audio = new Audio('assets/eta.wav');
      this.audio.muted = false;

      // new order
      this.audio2 = new Audio('assets/new_order.wav');
      this.audio2.muted = false;
    });

    this.watcher
      .on(POLL_UPDATE)
      .pipe(debounceTime(1000))
      .subscribe(() => {
        this.pollUpdateETAHandler();
        this.pollUpdateDineInHandler();
      });
  }

  public pollUpdateETAHandler() {
    // update the pending ETA list for non-Dinein
    const idsToIgnore: string[] = map(this._pendingOrdersWithNoEta, val => val.id);
    const noneDineInOrdersWithNoETA: Order[] = this.findAllOrdersWithNoEtaByContext(
      [CONTEXT_TYPE.Delivery, CONTEXT_TYPE.Drive_up, CONTEXT_TYPE.Pickup, CONTEXT_TYPE.Room_service],
      idsToIgnore,
    );

    if (size(noneDineInOrdersWithNoETA)) {
      each(noneDineInOrdersWithNoETA, order => {
        this._pendingOrdersWithNoEta.push({
          id: order.id,
          viewed: false,
        });
      });
    }

    const count: any = countBy(this._pendingOrdersWithNoEta, 'viewed');
    if (count['false'] && count['false'] > 0) {
      this.watcher.broadcast(ETA_PROMPT);
    }
  }

  public getPendingOrderWithNoETA(): Order {
    let retVal: Order;
    if (size(this._pendingOrdersWithNoEta)) {
      each(this._pendingOrdersWithNoEta, (order: { id: string; viewed: boolean }) => {
        if (!order.viewed) {
          const orderToCheck: Order = this.getOrderById(order.id);
          if (orderToCheck) {
            // confirm that it doesn't have an ETA
            if (!orderToCheck.eta) {
              // stop the loop
              retVal = orderToCheck;
              return false;
            } else {
              // mark it as viewed
              order.viewed = true;
            }
          } else {
            order.viewed = true;
          }
        }
      });
    }

    return retVal;
  }

  public removePendingOrderWithNoETA(id: string): void {
    if (size(this._pendingOrdersWithNoEta)) {
      for (let index: number = this._pendingOrdersWithNoEta.length - 1; index >= 0; index--) {
        const order: { id: string; viewed: boolean } = this._pendingOrdersWithNoEta[index];
        if (order.id == id) {
          order.viewed = true;
        }
      }
    }
  }
  /** PROMPT ORDER ETA QUEUE END */

  /** AUTO PRINTING QUEUE START */
  /**
   * Populates the two auto print backlog with orders orders that need to be auto printed
   */
  pollUpdateDineInHandler() {
    // update the pending list for Dinein
    const dineInIdsToIgnore: string[] = map(this._pendingDineInToAutoPrint, val => val.id);
    const dineInOrders: Order[] = this.findAllOrdersWithNoEtaByContext([CONTEXT_TYPE.Dine_in], dineInIdsToIgnore);

    if (size(dineInOrders)) {
      each(dineInOrders, order => {
        this._pendingDineInToAutoPrint.push({
          id: order.id,
          printed: false,
          failed: false,
        });
      });
    }

    const dineInCount: any = countBy(this._pendingDineInToAutoPrint, 'printed');
    if (dineInCount['false'] && dineInCount['false'] > 0) {
      this.watcher.broadcast(AUTO_PRINTING_ORDER);
    }

    // AUTO SET ETA = TRUE && PRINTED = FALSE
    const autoETAIdsToIgnore: string[] = map(this._pendingAutoSetETAtoPrint, val => val.id);
    const autoETAOrders: Order[] = this.findAllUnprintedSameDayOrdersForToday(autoETAIdsToIgnore);
    if (size(autoETAOrders)) {
      each(autoETAOrders, order => {
        this._pendingAutoSetETAtoPrint.push({
          id: order.id,
          printed: false,
          failed: false,
        });
      });
    }

    const autoETACount: any = countBy(this._pendingAutoSetETAtoPrint, 'printed');
    if (autoETACount['false'] && autoETACount['false'] > 0) {
      this.watcher.broadcast(AUTO_ETA_PRINTING_ORDER);
    }
  }

  /**
   * Retrieves a Dine-in order without ETA from the backlog
   */
  public getPendingDineInOrderToAutoPrint(): Order {
    let retVal: Order;

    // O@T
    if (size(this._pendingDineInToAutoPrint)) {
      each(this._pendingDineInToAutoPrint, (order: { id: string; printed: boolean; failed: boolean }) => {
        if (!order.printed && !order.failed) {
          const orderToCheck: Order = this.getOrderById(order.id);
          if (orderToCheck) {
            // confirm that it doesn't have an ETA
            if (!orderToCheck.eta) {
              // stop the loop
              retVal = orderToCheck;
              return false;
            } else {
              order.printed = true;
            }
          } else {
            order.printed = true;
          }
        }
      });
    }

    return retVal;
  }

  /**
   * Removes a Dine-in order without ETA from the backlog
   */
  public removePendingDineInOrderToAutoPrint(id: string, valueToSet: boolean = true, isFailed: boolean = false): void {
    // DINE IN
    if (size(this._pendingDineInToAutoPrint)) {
      for (let index: number = this._pendingDineInToAutoPrint.length - 1; index >= 0; index--) {
        const order: { id: string; printed: boolean; failed: boolean } = this._pendingDineInToAutoPrint[index];
        if (order.id == id) {
          order.printed = valueToSet;
          order.failed = isFailed;
        }
      }
    }
  }

  /**
   * Retrieves an AUTO SET ETA order that is not printed in the backlog
   */
  public getPendingAutoSetETAOrderToPrint(): Order {
    let retVal: Order;
    if (size(this._pendingAutoSetETAtoPrint)) {
      each(this._pendingAutoSetETAtoPrint, (orderQueue: { id: string; printed: boolean; failed: boolean }) => {
        if (!orderQueue.printed && !orderQueue.failed) {
          const orderToCheck: Order = this.getOrderById(orderQueue.id);
          if (orderToCheck) {
            // confirm that it is not yet PRINTED
            if (!orderToCheck.printed) {
              // stop the loop
              retVal = orderToCheck;
              return false;
            } else {
              orderQueue.printed = true;
            }
          } else {
            orderQueue.printed = true;
          }
        }
      });
    }

    return retVal;
  }

  /**
   * Removes an AUTO SET ETA order from the backlog
   */
  public removePendingAutoSetETAOrderToPrint(id: string, valueToSet: boolean = true, isFailed: boolean = false): void {
    if (size(this._pendingAutoSetETAtoPrint)) {
      for (let index: number = this._pendingAutoSetETAtoPrint.length - 1; index >= 0; index--) {
        const order: { id: string; printed: boolean; failed: boolean } = this._pendingAutoSetETAtoPrint[index];
        if (order.id == id) {
          order.printed = valueToSet;
          order.failed = isFailed;
        }
      }
    }
  }
  /** AUTO PRINTING QUEUE END */

  /**
   * Initialises the service and enables polling
   * @param urgent - Flagger to increase the polling rate. Not used yet.
   */
  public init(): void {
    // set the bump frequency
    this._bump_frequency = this.auth.Active_bump_screen.config.bump_frequency * 1000;

    // reset the polling
    this.reset();

    // start the eta check
    this.startETAPolling();

    // start listening to Order updates via SocketIO
    if (this.featureFlag.isFeatureFlagEnabled(FeatureFlagList.BFF) && this.socketIO.isSupported) {
      this._initSocketIO();
    } else {
      this._orderPolling();
    }
  }

  /**
   * Initialises the socket IO to accept docket events
   */
  private _initSocketIO() {
    this._socketIOSub = this.socketIO.connect(this.auth.Active_bump_screen).subscribe(
      (success: string) => {
        if (success === 'authorized') {
          this.analytics.trackEvent('Socket Authorization', 'Success', this.auth.Tracking_key);
          console.log('SOCKET AUTHORIZED');
        } else {
          console.log('SOCKET UNAUTHORIZED', 'Unknown reason');
          this.analytics.trackEvent('Socket Authorization', 'Failed: Unknown reason', this.auth.Tracking_key);
        }
      },
      reason => {
        console.log('SOCKET UNAUTHORIZED', reason);
        this.analytics.trackEvent('Socket Authorization', 'Failed:' + reason, this.auth.Tracking_key);
      },
    );

    // establish connection for AUTO SET ETA
    this._autoSetETADocketSub = this.socketIO.on(OMBffSocketEventType.DOCKET).subscribe((docketData: any) => {
      this.analytics.trackEvent(
        'BFF Docket received',
        `${docketData.order_id}, End: ${Date.now()}`,
        this.auth.Tracking_key,
      );
      this.generatedDocketRepo.addDocketToRepository(docketData);
    });

    // pull the cached dockets from BFF
    const getCacheDate = new Date();
    this.bffService
      .getCachedDockets()
      .then((payload: any) => {
        if (payload && payload.success && size(payload.results)) {
          this.generatedDocketRepo.addMultipleGeneratedDockets(payload.results);
          this.updateVipTags();
        }
        this.analytics.trackEvent(
          'Docket Cache',
          `Duration: ${new Date().valueOf() - getCacheDate.valueOf()}`,
          this.auth.Tracking_key,
        );
        return;
      })
      .then(() => {
        this._orderPolling();
      });

    this.vipService.vipsUpdatedEvent.subscribe(() => {
      this.updateVipTags();
    });
    this.vipService.loadVips(this.auth.Active_bump_screen.account_id);
  }

  private updateVipTags() {
    this._orders.forEach((order: Order) => {
      order.vip = this.vipService.checkOrderForVip(order);
    });
  }
  /**
   * Starts the polling loop that request a HTTP poll for every tick
   * @param delay - Add a bump frequency delay on the first call. Defaults to FALSE
   */
  private _orderPolling(delay: boolean = false): void {
    this._stopped = false;
    this._interval = interval(this._bump_frequency)
      .pipe(startWith(delay ? this._bump_frequency : 0))
      .subscribe(() => {
        this.requestOrderList();
      });
  }

  /**
   * Sends a HTTP poll request to the server
   */
  public requestOrderList = (force: boolean = false) => {
    this._polling = true;

    if (!this._stopped || force) {
      // cancel the previous request
      if (this._pollSub) this._pollSub.unsubscribe();

      let sendObj: any = {
        bump_screen_id: this.auth.Active_bump_screen.id,
      };

      const header = new HttpHeaders(this.auth.headerData);
      this._pollSub = this.comms.post(
        this.core.Active_server.url + 'api/bump/orders/list/v2/' + this.auth.Active_bump_screen.id,
        sendObj,
        { headers: header },
        payload => {
          this._pollSuccessHandler(payload, force);
        },
        this._pollFailHandler,
        this._bump_frequency,
      );
    }
  };

  /**
   * Handles the successful responses from the poll request
   * @param payload - Payload successful response
   */
  private _pollSuccessHandler = (payload, force: boolean = false) => {
    this._polling = false;

    if (!this._stopped || force) {
      // Set server time
      if (has(payload, 'current_date')) {
        this.core.Timezone = this.auth.Active_bump_screen.location_details[0].timezone.replace('|', '/');
      }

      if (payload && has(payload, 'success') && payload.success) {
        // update the active order
        this.updateOrders(payload.orders);
      } else {
        // TODO - register a fail
      }

      // fire to inform app that a poll had just been completed
      this.firstPollComplete = true;
      this.watcher.broadcast(POLL_COMPLETED);
    }
  };

  /**
   * Handles the failed responses from the poll request
   * @param fail - Payload failed response
   */
  private _pollFailHandler = fail => {
    this._polling = false;

    if (!this._stopped) {
      // TODO - register a fail
    }
  };

  public startETAPolling(): void {
    clearTimeout(this.ETACheckRef);
    this.ETACheckRef = setTimeout(this.checkDocketsWithNoETA, this.ETA_INTERVAL);
  }

  /**
   * Checks if there are any new orders with no ETA. It will play the notification sound
   * if at least 1 is found.
   * */
  checkDocketsWithNoETA = () => {
    clearTimeout(this.ETACheckRef);
    let notify: boolean = false;

    // reset the interval to maximum interval
    this.ETA_INTERVAL = this.MAX_ETA_INTERVAL;

    const showYesterday: boolean = this.appSettings.getBumpScreenSettingByKey('general', 'show_yesterday_orders');
    const now = this.core.Server_time;

    const ordersToCheck: Order[] = this.getNewOrders(false);
    const unattendedItems: Order[] = find(ordersToCheck, (order: Order) => {
      const timeDiff: number = moment
        .duration(now.diff(UtilityClass.parseTimeToTimezone(order.created_time, this.core.Timezone)))
        .asMinutes();
      if (timeDiff >= this.ETA_MINUTE_CHECK) {
        const timeToUse: string = order.requested_time ? order.requested_time : order.created_time;
        const requestTime: moment.Moment = UtilityClass.parseTimeToTimezone(timeToUse, this.core.Timezone);
        const includedAsYesterday: boolean = showYesterday || requestTime.isSameOrAfter(now, 'day');
        if (includedAsYesterday) {
          const hasETA: boolean = size(order.eta) > 0;
          return !hasETA;
        }
      }

      return false;
    });

    // if there are any unattended items,
    // annoy the hell of the user and start reducing the timeout interval
    if (unattendedItems) {
      notify = true;

      this.ETA_INTERVAL =
        this.ETA_LOOP_COUNTER == 1
          ? this.MID_ETA_INTERVAL
          : this.ETA_LOOP_COUNTER < 4
          ? this.MID_ETA_INTERVAL
          : this.MIN_ETA_INTERVAL;

      this.ETA_LOOP_COUNTER++;
    } else {
      this.ETA_LOOP_COUNTER = 1;
    }

    // queue next eta check
    this.ETACheckRef = setTimeout(this.checkDocketsWithNoETA, this.ETA_INTERVAL);

    //play the sound
    if (notify) {
      this.playNotificationSound('eta');
    }
  };

  /**
   * Plays the notification sound based on trigger
   * @param trigger - The type of notification sound to play
   */
  async playNotificationSound(trigger: 'eta' | 'new') {
    // set the max volume
    await this.appSettings.pluginSettings('force_max_volume');

    try {
      if (trigger == 'eta') {
        this.watcher.broadcast(PLAY_SOUND_ETA, this.ETA_LOOP_COUNTER - 1);
        if (!this.platform.is('cordova')) {
          this.audio.play();
        } else {
          try {
            this.nativeAudio.play('notification');
          } catch (e) {
            this.analytics.trackEvent('Sound', 'Unable to play ETA sound:' + e.message, this.auth.Tracking_key);
            this.audio.play();
          }
        }
      } else if (trigger == 'new') {
        this.watcher.broadcast(PLAY_SOUND_NEW);
        if (!this.platform.is('cordova')) {
          this.audio2.play();
        } else {
          try {
            this.nativeAudio.play('notification2');
          } catch (e) {
            this.analytics.trackEvent('Sound', 'Unable to play NEW sound:' + e.message, this.auth.Tracking_key);
            this.audio2.play();
          }
        }
      }
    } catch (e) {
      // capture chrome errors
      console.log('PLAY ERR', e);
    }

    return;
  }
  /**
   * Reset the state of the service to initial state
   */
  public reset(): void {
    this._stopped = true;

    if (this._interval) this._interval.unsubscribe();
    if (this._pollSub) this._pollSub.unsubscribe();
    if (this._socketIOSub) this._socketIOSub.unsubscribe();
    if (this._autoSetETADocketSub) this._autoSetETADocketSub.unsubscribe();
    this.socketIO.disconnect();
    this._orders = [];
    this._pendingOrdersWithNoEta = [];
    this._pendingDineInToAutoPrint = [];
    this._pendingAutoSetETAtoPrint = [];

    this._active_order = null;
    this.firstPollComplete = false;

    // remove eta check
    clearTimeout(this.ETACheckRef);

    // fire a global update
    this.watcher.broadcast(POLL_UPDATE);
  }

  /*** DOCKET MANAGEMENT ***/
  getAllOrdersByFilters(contextFilters: string[], bumpstatusFilter: string, dayFilters: string[]): Order[] {
    let orders: Order[] = [];

    // bump status
    if (size(bumpstatusFilter)) {
      if (bumpstatusFilter == 'bumped') {
        orders = this.getBumpedOrders();
      } else {
        orders = this.getNewOrders(true, bumpstatusFilter);
      }
    }

    if (size(contextFilters) && contextFilters.indexOf('all') == -1) {
      orders = filter(orders, (order: Order) => contextFilters.indexOf(order.filter_module) > -1);
    }

    if (size(dayFilters)) {
      orders = filter(orders, (order: Order) => {
        // format will be YYYY-MM-DD
        const orderTimeToUse: string = (order.eta ? order.eta : order.requested_time).substr(0, 10);
        return dayFilters.indexOf(orderTimeToUse) > -1;
      });
    }

    return orders;
  }

  public findOrderWithNoEta(immediate: boolean, ignoreIds: string[] = [], ignoreContext: string[] = []): Order {
    ignoreContext = size(ignoreContext) == 0 ? [] : ignoreContext;
    const method = (docket: Order) => {
      if (!docket) return false;

      return (
        (!ignoreContext || ignoreContext.indexOf(docket.filter_module) == -1) &&
        docket.bump_status == 'new' &&
        !docket.eta &&
        (immediate || ignoreIds.indexOf(docket.id) == -1)
      );
    };

    // get the old one first
    const result: Order = findLast(this._orders, method);
    return result;
  }

  public findAllOrdersWithNoEta(ignoreContext: string[] = []): Order[] {
    return filter(this._orders, (docket: Order) => {
      return (
        (!ignoreContext || ignoreContext.indexOf(docket.filter_module) == -1) &&
        docket.bump_status == 'new' &&
        !docket.eta
      );
    });
  }

  public findOrderWithEta(): Order {
    const method = (docket: Order) => {
      return docket.bump_status == 'new' && docket.eta;
    };

    // get the old one first
    const result: Order = findLast(this._orders, method);
    return result;
  }

  public findAllOrdersWithEta(): Order[] {
    return filter(this._orders, (docket: Order) => {
      return docket.bump_status == 'new' && docket.eta;
    });
  }

  public findAllFutureOrdersForToday(): Order[] {
    const now = +this.core.Server_time.format('YYYYMMDD');
    let orders: Order[] = this.findAllOrdersWithEta();
    orders = filter(orders, (order: Order) => {
      const subCreatedTime: number = UtilityClass.getOrderDateToNumber(order.created_time);
      const subEtaTime: number = UtilityClass.getOrderDateToNumber(order.eta);

      // only print orders for today and yesterday
      return order.auto_set_eta && subEtaTime == now && subCreatedTime < now;
    });

    return orders;
  }

  /**
   * Finds all orders with ETA set automatically. This will only retrieve the ones ordered on the same day.
   */
  public findAllUnprintedSameDayOrdersForToday(ignoreIds: string[] = []): Order[] {
    const now = +this.core.Server_time.format('YYYYMMDD');
    let orders: Order[] = this.findAllOrdersWithEta();
    orders = filter(orders, (order: Order) => {
      const subCreatedTime: number = UtilityClass.getOrderDateToNumber(order.created_time);
      const subEtaTime: number = UtilityClass.getOrderDateToNumber(order.eta);

      // only print orders created today
      return (
        ignoreIds.indexOf(order.id) == -1 &&
        order.auto_set_eta &&
        !order.printed &&
        subEtaTime == now &&
        subCreatedTime == now
      );
    });

    return orders;
  }

  public findOrderWithNoEtaByContext(contexts: string[], immediate: boolean, ignoreIds: string[] = []): Order {
    const method = (docket: Order) => {
      if (!docket) return false;

      return (
        contexts.indexOf(docket.filter_module) > -1 &&
        docket.bump_status == 'new' &&
        !docket.eta &&
        (immediate || ignoreIds.indexOf(docket.id) == -1)
      );
    };

    // get the old one first
    const result: Order = findLast(this._orders, method);
    return result;
  }

  public findAllOrdersWithNoEtaByContext(contexts: string[], ignoreIds: string[] = []): Order[] {
    const method = (docket: Order) => {
      return (
        contexts.indexOf(docket.filter_module) > -1 &&
        docket.bump_status == 'new' &&
        !docket.eta &&
        ignoreIds.indexOf(docket.id) == -1
      );
    };

    // get the old one first
    const orders: Order[] = filter(this._orders, method);
    return orders;
  }

  /**
   * Update the active orders with new data based on last updated property
   * @param orders - The raw/json orders
   */
  public updateOrders(orders: any, removeNonExistingOrders: boolean = true): void {
    const activeOrderId: string = this.Active_order ? this.Active_order.id : null;
    const showYesterday: boolean = this.appSettings.getBumpScreenSettingByKey('general', 'show_yesterday_orders');
    const now = this.core.Server_time;
    const nowFormatted: string = this.core.Server_time.format('YYYYMMDD');

    let updated: boolean = false;
    let willPlayNewOrderSound: boolean = false;

    // subtract 1 day for - YESTERDAY
    const allowedTime: moment.Moment = moment(
      this.core.Server_time.subtract(1, 'day').format('YYYY-MM-DD'),
      'YYYY-MM-DD',
    );
    const strAllowedTime: number = +allowedTime.format('YYYYMMDD');
    if (orders) {
      // trim and only get orders that are within acceptable time
      orders = filter(orders, order => {
        let orderDateReference: string;
        if (order.eta) {
          orderDateReference = order.eta;
        } else if (order.requested_time) {
          orderDateReference = order.requested_time;
        } else {
          orderDateReference = order.created_time;
        }

        const orderNumDateReference: number = UtilityClass.getOrderDateToNumber(orderDateReference);
        return orderNumDateReference >= strAllowedTime;
      });

      // strip old orders
      each(orders, (order: any) => {
        const newOrder: Order = new Order(order);

        // check if it has last updated - backward compatibility, do not assume it's always there
        if (!newOrder.last_updated_time) {
          newOrder['last_updated_time'] = this.core.Server_time.valueOf();
        }

        const storedOrder: Order = this.getOrderById(newOrder.id);
        // check if order already exist
        if (storedOrder) {
          // if the polling value is updated, then replace it
          if (storedOrder.last_updated_time < newOrder.last_updated_time) {
            // replace it here
            // only update an order when there's a change on its dynamic property
            this.updateOrder(newOrder, [
              'eta',
              'bump_status',
              'last_updated_time',
              'receipt',
              'printed',
              'auto_set_eta',
              'split_receipt',
              'split_dockets',
              'collect_at_counter_sms',
              'collect_at_counter_sms_food',
              'collect_at_counter_sms_drink',
              'refunded',
              'refunded_amt',
              'failed_output',
              'failed_output_reason',
            ]);
            updated = true;
          }
        } else {
          // check if order doesn't exist
          if (newOrder.bump_status == 'new') {
            if (!newOrder.eta) {
              newOrder.pristine = true;
            } else {
              if (!newOrder.printed) {
                // only play the sound for orders TODAY
                if (newOrder.auto_set_eta || newOrder.is_delivery_window) {
                  const subRequestedTime: number = UtilityClass.getOrderDateToNumber(newOrder.requested_time);
                  const subCreatedTime: number = UtilityClass.getOrderDateToNumber(newOrder.created_time);
                  newOrder.pristine = subCreatedTime == +nowFormatted && subCreatedTime == subRequestedTime;
                }
              }
            }
          }
          newOrder.vip = this.vipService.checkOrderForVip(newOrder);
          // new order added
          this._orders.push(newOrder);
          updated = true;

          console.log(
            'New Order Arrived: ' +
              newOrder.id +
              ' | Polled time: ' +
              this.core.Server_time.format('YYYY-MM-DD hh:mm:ss'),
            this.auth.Tracking_key,
          );

          this.analytics.trackEvent(
            'New Order Arrived',
            `Order Id: ${newOrder.id} | Polled time: ${this.core.Server_time.format('YYYY-MM-DD hh:mm:ss')}`,
            this.auth.Tracking_key,
          );

          // do not check if flagger is already set to TRUE
          if (!willPlayNewOrderSound) {
            const timeToUse: string = order.requested_time ? order.requested_time : order.created_time;
            const requestTime: moment.Moment = UtilityClass.parseTimeToTimezone(timeToUse, this.core.Timezone);
            const includedInSearch: boolean = showYesterday || requestTime.isSameOrAfter(now, 'day');
            willPlayNewOrderSound = includedInSearch && newOrder.pristine;
          }
        }
      });
    }

    // remove non existing orders
    if (removeNonExistingOrders) {
      // trim and only get orders that are within acceptable time
      const oldKeys: string[] = map(this._orders, (order: Order) => order.id);
      const newKeys: string[] = map(orders, (order: Order) => order.id);
      const filteredKeys: string[] = oldKeys.filter(id => newKeys.indexOf(id) === -1);
      this.removeOrderByIds(filteredKeys);
      this.generatedDocketRepo.removeGeneratedDocketByID(filteredKeys);
    }

    // Play the new order sound
    if (willPlayNewOrderSound) {
      this.playNotificationSound('new');
    }

    if (updated) {
      // update the active order
      if (activeOrderId && size(orders)) {
        this._active_order = this.getOrderById(activeOrderId);
      } else {
        this._active_order = null;
      }

      // fire if there are changes on the dockets
      this.watcher.broadcast(POLL_UPDATE);
    }
  }

  public removeOrderByIds(ids: string[]): void {
    each(ids, (id: string) => {
      const orderIndex: number = findIndex(this._orders, { id: id });
      if (orderIndex > -1) {
        this._orders.splice(orderIndex, 1);
      }
    });
  }

  /**
   * Retrieves an order by ID
   * @param id - The ID of the order
   */
  public getOrderById(id: string): Order {
    const order: Order = find(this._orders, ['id', id]);
    return order;
  }

  /**
   * Replaces the order with a new value
   * @param newOrder - The new order to replace
   */
  public updateOrder(newOrder: Order, props: string[]): void {
    let index: number = findIndex(this._orders, { id: newOrder.id });
    if (index > -1) {
      if (size(props)) {
        let order: Order = this._orders[index];
        each(props, prop => {
          if (typeof prop === 'string' && has(order, prop)) {
            // LOG IF PRINTED GOT SET TO FALSE
            if (prop == 'printed' && order.printed && !newOrder.printed) {
              this.analytics.trackEvent(
                'Order Update',
                'Printed set to FALSE:' + order.id,
                this.auth.Tracking_key,
                true,
              );
            }

            // LOG IF ETA GOT REMOVED
            if (prop == 'eta' && order.eta && !newOrder.eta) {
              this.analytics.trackEvent(
                'Order Update',
                'ETA removed:' +
                  order.id +
                  ' | Updated times: (NEW)' +
                  newOrder.last_updated_time +
                  ' (OLD)' +
                  order.last_updated_time,
                this.auth.Tracking_key,
                true,
              );
            }

            order[prop] = newOrder[prop];
          }
        });
      } else {
        this._orders.splice(index, 1, newOrder);
      }
    }
  }

  /**
   * Set the active order
   * @param id - The ID of the order
   */
  public setActiveOrderById(id: string): void {
    const currentOrderId: string = this._active_order ? this._active_order.id : null;

    const order: Order = find(this._orders, ['id', id]);
    this._active_order = order;

    const newOrderId: string = this._active_order ? this._active_order.id : null;

    if (currentOrderId != newOrderId) {
      this.watcher.broadcast(ACTIVE_ORDER_UPDATED, this._active_order);
    }
  }

  /**
   * Retrieve all the orders with "new" bump status
   */
  public getNewOrders(toFilter: boolean, state: string = 'new'): Order[] {
    const newOrders: Order[] = filter(this._orders, ['bump_status', 'new']);
    if (toFilter) {
      return filter(newOrders, (order: Order) => order.orderFilterBumpState() == state);
    } else {
      return newOrders;
    }
  }

  /**
   * Retrieve all the orders with "bumped" bump status
   */
  public getBumpedOrders(): Order[] {
    const bumpedOrders: Order[] = filter(this._orders, ['bump_status', 'bumped']);
    return bumpedOrders;
  }

  /**
   *
   * @param newOrder
   * @param props
   */
  public getOrdersByDate(start: string, end?: string, ordersToUse?: Order[]): Order[] {
    // DO NOT USE MOMENT TO ANALYSE THE TIME
    let retVal: Order[] = [];

    // get source target
    const source: Order[] = !isNil(ordersToUse) ? ordersToUse : this.getAllOrders();
    if (size(source)) {
      const startDate = new Date(start).valueOf();
      const endDate = size(end) ? new Date(end).getTime() : null;
      if (end) {
        retVal = source.filter((order: Order) => {
          const dateString: string = size(order.eta) ? order.eta : order.requested_time;
          const orderReference = new Date(dateString.substr(0, 10)).valueOf();
          return orderReference >= startDate && orderReference <= endDate;
        });
      } else {
        retVal = source.filter((order: Order) => {
          const dateString: string = size(order.eta) ? order.eta : order.requested_time;
          const dateSub: string = dateString.substr(0, 10);
          const orderReference = new Date(dateSub).valueOf();

          if (endDate) {
            return orderReference >= startDate && orderReference <= endDate;
          } else {
            return orderReference == startDate || dateSub == start;
          }
        });
      }
    }

    return retVal;
  }

  public getAllCounts(bumpStatus: string = 'new'): any {
    let retVal;

    if (size(this._orders)) {
      let ordersToCount: Order[] = [];
      if (bumpStatus == 'bumped') {
        ordersToCount = this.getBumpedOrders();
      } else {
        ordersToCount = this.getNewOrders(true, bumpStatus);
      }

      // show yesterday - cleanup
      const showYesterday: boolean = this.appSettings.getBumpScreenSettingByKey('general', 'show_yesterday_orders');
      if (!showYesterday) {
        const now: string = this.core.Server_time.format('YYYYMMDD');
        ordersToCount = filter(ordersToCount, (order: Order) => {
          const dayRef: string = order.eta ? order.eta : order.requested_time;
          const strDayRef: number = UtilityClass.getOrderDateToNumber(dayRef);
          return strDayRef >= +now;
        });
      }

      if (size(ordersToCount)) {
        retVal = {
          all: 0,
          contexts: {},
          noEta: {},
        };
        retVal['contexts'] = countBy(ordersToCount, 'filter_module');

        // add all orders
        retVal['all'] = size(ordersToCount);

        // count items without ETA
        const noEtaByContext = {};
        each(ordersToCount, (order: Order) => {
          if (!order.eta) {
            const ctxType: string = order.filter_module;
            if (has(noEtaByContext, ctxType)) noEtaByContext[ctxType]++;
            else noEtaByContext[ctxType] = 1;
          }
        });
        retVal['noEta'] = noEtaByContext;
      }
    }

    return retVal;
  }

  public getWeekCountsByStatus(
    bumpFilter: string,
    contextFilter: string,
    range: string[] = ['bumps', 'contexts', 'days', 'noEtas', 'prevOrders', 'nextOrders'],
  ): any {
    let retVal = {
      total: 0,
      bumps: {},
      contexts: {},
      days: {},
      noETAByContext: {},
      noETAByDate: {},
      prevOrders: 0,
      nextOrders: 0,
    };

    const totalWeekCount: number = size(this.filterService.Preorder_weeks);
    const preorderWeekIndex: number = this.filterService.Preorder_week_index;
    if (totalWeekCount > 0) {
      const activePreorderWeek: IPreorderWeek = this.filterService.Preorder_weeks[preorderWeekIndex];
      if (activePreorderWeek) {
        // grab the start and end of the week
        const startDate: string = activePreorderWeek.start;
        const endDate: string = activePreorderWeek.end;

        // get fixed first date and last date for counting the NEXT and PREV orders
        const startBeforeDate: string = this.filterService.Preorder_weeks[0].start;
        const endAfterDate: string = this.filterService.Preorder_weeks[totalWeekCount - 1].end;

        // limit the orders to count based on bump status asked
        let ordersToUse: Order[];
        if (bumpFilter == 'bumped') {
          ordersToUse = [...this.getBumpedOrders()];
        } else if (bumpFilter == 'new' || bumpFilter == 'current') {
          ordersToUse = [...this.getNewOrders(true, bumpFilter)];
        } else {
          ordersToUse = [...this.getAllOrders()];
        }

        if (size(contextFilter) && contextFilter != 'all') {
          ordersToUse = filter(ordersToUse, (order: Order) => order.filter_module == contextFilter);
        }

        // START THE COUNT

        // filter the number of orders based on the start and date of current week filter
        const ordersToCount: Order[] = this.getOrdersByDate(startDate, endDate, ordersToUse);
        retVal.total = size(ordersToCount);

        // now make a count by context type
        if (range && range.indexOf('contexts') > -1) {
          retVal.contexts = countBy(ordersToCount, 'context_type');
        } else {
          delete retVal.contexts;
        }

        // now make a count by date
        if (range && range.indexOf('days') > -1) {
          retVal.days = countBy(ordersToCount, (order: Order) => {
            const dateRef: string = order.eta ? order.eta : order.requested_time;
            return dateRef.substr(0, 10);
          });
        } else {
          delete retVal.days;
        }

        // now make a count by bump status
        if (range && range.indexOf('bumps') > -1) {
          retVal.bumps = countBy(ordersToCount, 'bump_status');
        } else {
          delete retVal.bumps;
        }

        if (range && range.indexOf('noEtas') > -1) {
          // count items without ETA
          each(ordersToCount, (order: Order) => {
            if (!order.eta) {
              const ctxType: string = order.filter_module;
              const dateRef: string = order.requested_time.substr(0, 10);

              if (has(retVal.noETAByContext, ctxType)) retVal.noETAByContext[ctxType]++;
              else retVal.noETAByContext[ctxType] = 1;

              if (has(retVal.noETAByDate, dateRef)) retVal.noETAByDate[dateRef]++;
              else retVal.noETAByDate[dateRef] = 1;
            }
          });
        } else {
          delete retVal.noETAByContext;
          delete retVal.noETAByDate;
        }

        if (range && range.indexOf('prevOrders') > -1) {
          // count the number of orders BEFORE the active week filter
          if (preorderWeekIndex > 0) {
            const endBeforeDate: string = this.filterService.Preorder_weeks[preorderWeekIndex - 1].end;
            const beforeOrders: Order[] = this.getOrdersByDate(startBeforeDate, endBeforeDate, ordersToUse);
            retVal.prevOrders = size(beforeOrders);
          }
        } else {
          delete retVal.prevOrders;
        }

        if (range && range.indexOf('nextOrders') > -1) {
          // count the number of orders AFTER the active week filter
          if (preorderWeekIndex < totalWeekCount - 1) {
            const startAfterDate: string = this.filterService.Preorder_weeks[preorderWeekIndex + 1].start;
            const afterOrders: Order[] = this.getOrdersByDate(startAfterDate, endAfterDate, ordersToUse);
            retVal.nextOrders = size(afterOrders);
          }
        } else {
          delete retVal.nextOrders;
        }
      }
    }

    return retVal;
  }

  orderFilterCountSummary(): void {
    this.watcher.broadcast(ORDER_COUNT_CHANGED);
  }

  /**
   * Send a request to the server to update the order ETA of multiple single orders (not batch orders)
   * @param orderReferences - The ETA to set
   */
  public groupSingleOrderETAUpdate(orderReferences: { id: string; eta: string; action: string }[]): Promise<any> {
    return new Promise(resolve => {
      let updatedOrders: { id: string; eta: string; action: string }[] = [];

      each(orderReferences, (order: { id: string; eta: string; action: string }) => {
        const orderToUpdate: Order = this.getOrderById(order.id);
        if (orderToUpdate && orderToUpdate.type != 'batch_order') {
          orderToUpdate.eta = order.eta;
          updatedOrders.push(order);
        }
      });

      // force a list update
      this.watcher.broadcast(APP_DOCKET_DISPLAY_UPDATE, 'groupSingleOrderETAUpdate');
      this.orderFilterCountSummary();

      // setup object to send - with bump screen ID, return order obj :NO, & ETA array
      let sendObj: any = {
        bump_screen_id: this.auth.Active_bump_screen.id,
        return_orders: true, // force it true for now - it needs the new docket with ETA
        type: null,
        orders: updatedOrders,
      };

      const header = new HttpHeaders(this.auth.headerData);

      // force 20 seconds timeout for setting ETA
      this.comms.post(
        this.core.Active_server.url + 'api/bump/orders/v3',
        sendObj,
        {
          headers: header,
        },
        payload => {
          // block propagation of response if user already logged out
          if (!this.auth.Logged_in) {
            resolve(false);
            return;
          }

          if (payload) {
            let failedFound: boolean = false;

            // if the backend returned new orders, save it
            if (size(payload.orders)) {
              this.updateOrders(payload.orders, false);
            }

            // loop through orders and set the ETA
            if (size(payload.result)) {
              each(payload.result, result => {
                if (!payload.success) {
                  failedFound = true;
                  this.resetDocket(result.id, 'eta', result.eta);
                }
              });
            } else {
              // there's an error somewhere
              resolve(false);
              return;
            }

            resolve(!failedFound);
          } else {
            // if responded an error - clear the ETA of orders
            each(updatedOrders, order => {
              this.resetDocket(order.id, 'eta');
            });

            resolve(false);
          }
        },
        fail => {
          // block propagation of response if user already logged out
          if (!this.auth.Logged_in) {
            resolve(false);
            return;
          }

          each(updatedOrders, order => {
            this.resetDocket(order.id, 'eta');
          });

          resolve(false);
        },
        30000,
      );
    });
  }

  /**
   * Reset to the previous of the order after a failed request
   * @param docketId - The docket id
   * @param fromMethod - The method request that failed
   */
  resetDocket(docketId: string, fromMethod: string, data?: any): void {
    const order: Order = this.getOrderById(docketId);
    if (order) {
      // unbump the docket
      if (fromMethod === 'bump') {
        order.bump_status = 'new';
      }

      // bump the docket
      if (fromMethod === 'unbump') {
        order.bump_status = 'bumped';
      }

      //set the docket ETA back to ''
      if (fromMethod === 'eta' || fromMethod === 'confirm') {
        order.eta = data ? data : '';
        order.bump_status = 'new';

        // do not kill off the pending
        // if the backend returned with an eta as part of error message
        if (size(data) == 0) {
          order.setPending(false);
        }
      }

      //set the docket SMS back to null
      if (fromMethod === 'sms') {
        // Only reset the legacy SMS field, or the specific food/drink field that failed
        if (data && data.smsType) {
          // Food/drink SMS failure - restore the original value (or null if it was a new attempt)
          if (data.smsType === 'food') {
            order.collect_at_counter_sms_food = data.originalValue !== undefined ? data.originalValue : null;
          } else if (data.smsType === 'drink') {
            order.collect_at_counter_sms_drink = data.originalValue !== undefined ? data.originalValue : null;
          }
        } else {
          // Legacy SMS failure - reset the legacy field only
          order.collect_at_counter_sms = null;
        }
      }

      this.watcher.broadcast(POLL_UPDATE);
    }
  }

  /*** AUDIO ***/

  /** Creates the docket notification audio */
  checkNativeAudio(): void {
    if (this.platform.is('cordova')) {
      this.nativeAudio.preloadSimple('notification', 'assets/eta.wav');
      this.nativeAudio.preloadSimple('notification2', 'assets/new_order.wav');
    }
  }

  /*** GETTERS AND SETTERS ****/
  public get Active_order(): Order {
    return this._active_order;
  }

  public get Polling(): boolean {
    return this._polling;
  }
  /**
   * Retrieve all the available orders
   */
  public getAllOrders(): Order[] {
    return this._orders;
  }
}
